import type { Permission } from '@mm/shared';
import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { EmptyState, Spinner } from '../components/ui/display';
import { useAuth } from '../features/auth/AuthProvider';
import { LockScreen } from '../features/auth/LockScreen';

/**
 * Layout route: renders children only for signed-in users. A session locked
 * after inactivity keeps the page underneath (unsaved input survives) behind
 * the lock screen; a locked session found on page load shows only the lock.
 */
export function ProtectedRoute() {
  const { status, user } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <Spinner label="Restoring session…" className="min-h-screen" />;
  if (status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  if (status === 'locked' && !user) return <LockScreen />;
  return (
    <>
      <div inert={status === 'locked'}>
        <Outlet />
      </div>
      {status === 'locked' && <LockScreen />}
    </>
  );
}

/** Hides a page from roles without the permission (the API enforces it as well). */
export function RequirePermission({
  permission,
  children,
}: {
  permission: Permission;
  children: ReactNode;
}) {
  const { can } = useAuth();
  if (!can(permission)) {
    return (
      <EmptyState
        title="You don't have access to this page"
        description="Ask an administrator if you need this permission."
      />
    );
  }
  return <>{children}</>;
}
